import { useState } from 'react';
import { MessageDescriptor } from '@lingui/core';
import { msg } from '@lingui/core/macro';
import { Trans, useLingui } from '@lingui/react/macro';

import { Endpoints } from '@/api';
import Button from '@/design_system/Button';
import Dialog from '@/design_system/Dialog';
import { Footer, FooterButtons } from '@/design_system/Footer';
import { Label } from '@/design_system/Label/Label';
import { OptionalLabel } from '@/design_system/Label/OptionalLabel';
import { RadioBox, RadioBoxGroup } from '@/design_system/Radio/RadioBox/RadioBox';
import Stack from '@/design_system/Stack';
import TextArea from '@/design_system/TextArea';
import { useArticleContext } from '@/routes/Requests/contexts/ArticleContext';

export const REPAIR_ISSUES: {
  id: Endpoints['POST /articles/:id/exit-repair']['body']['issue'];
  label: MessageDescriptor;
  onBehalfLabel: MessageDescriptor;
  requireComment: boolean;
}[] = [
  {
    id: 'item-too-damaged',
    label: msg({
      id: 'article.actions.repair_article.report_repair_issue.item-too-damaged.text',
      message: 'The item is too damaged to be handled',
    }),
    onBehalfLabel: msg({
      id: 'article.actions.repair_article.report_repair_issue.item-too-damaged.text',
      message: 'The item is too damaged to be handled',
    }),
    requireComment: false,
  },
  {
    id: 'actions-wrongly-specified',
    label: msg({
      id: 'article.actions.repair_article.report_repair_issue.actions-wrongly-specified.text',
      message: "Actions were wrongly specified and I can't handle them",
    }),
    onBehalfLabel: msg({
      id: 'article.actions.repair_article.report_repair_issue.actions-wrongly-specified.text.on-behalf',
      message: "The actions were wrongly specified and the workshop can't handle them",
    }),
    requireComment: false,
  },
  {
    id: 'not-enough-time',
    label: msg({
      id: 'article.actions.repair_article.report_repair_issue.not-enough-time.text',
      message: "I don't have enough time/resources to handle the item",
    }),
    onBehalfLabel: msg({
      id: 'article.actions.repair_article.report_repair_issue.not-enough-time.text.on-behalf',
      message: "The workshop doesn't have enough time/resources to handle the item",
    }),
    requireComment: false,
  },
  {
    id: 'damage-during-repair',
    label: msg({
      id: 'article.actions.repair_article.report_repair_issue.damage-during-repair.text',
      message: 'A damage occurred during the repair process',
    }),
    onBehalfLabel: msg({
      id: 'article.actions.repair_article.report_repair_issue.damage-during-repair.text.on-behalf',
      message: 'The workshop damaged the item during the repair process',
    }),
    requireComment: true,
  },
  {
    id: 'custom',
    label: msg({
      id: 'article.actions.repair_article.report_repair_issue.custom.text',
      message: 'Other reason, please specify',
    }),
    onBehalfLabel: msg({
      id: 'article.actions.repair_article.report_repair_issue.custom.text',
      message: 'Other reason, please specify',
    }),
    requireComment: true,
  },
];

export const ReportRepairIssueDialog = ({
  isOpen,
  onClose,
  onConfirm,
  isLoading,
}: {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: (data: Endpoints['POST /articles/:id/exit-repair']['body']) => void;
  isLoading: boolean;
}) => {
  return (
    <Dialog
      isOpen={isOpen}
      title={
        <Trans id="article.actions.repair_article.report_repair_issue.title">
          Report a repair issue
        </Trans>
      }
      onOpenChange={onClose}
    >
      <ReportRepairIssueForm
        onConfirm={(data) => {
          onConfirm(data);
          onClose();
        }}
        isLoading={isLoading}
      />
    </Dialog>
  );
};

const ReportRepairIssueForm = ({
  onConfirm,
  isLoading,
}: {
  onConfirm: (data: Endpoints['POST /articles/:id/exit-repair']['body']) => void;
  isLoading: boolean;
}) => {
  const { view, iam } = useArticleContext();
  const { t, i18n } = useLingui();
  const [showErrors, setShowErrors] = useState(false);
  let repairIssues = REPAIR_ISSUES;

  const [issue, setIssue] =
    useState<Endpoints['POST /articles/:id/exit-repair']['body']['issue']>();
  const [comment, setComment] =
    useState<Endpoints['POST /articles/:id/exit-repair']['body']['comment']>();

  if (view.services.actions.fields.spareParts.shown) {
    repairIssues = REPAIR_ISSUES.toSpliced(REPAIR_ISSUES.length - 1, 0, {
      id: 'need-additional-spare-parts',
      label: msg({
        id: 'article.actions.repair_article.report_repair_issue.need-additional-spare-parts.text',
        message: 'I need to add paid spare parts',
      }),
      onBehalfLabel: msg({
        id: 'article.actions.repair_article.report_repair_issue.need-additional-spare-parts.text.on-behalf',
        message: 'The workshop needs to add paid spare parts',
      }),
      requireComment: false,
    });
  }

  const issueData = REPAIR_ISSUES.find(({ id }) => id === issue);

  return (
    <>
      <main>
        <Stack gap="1rem">
          <Label
            label={t({
              id: 'article.actions.repair_article.report_repair_issue.label',
              message:
                "You're about to report an issue and refuse the job. Please specify your refusal reason:",
            })}
          >
            <RadioBoxGroup
              name="report-repair-issue"
              value={issue}
              onChange={setIssue}
              direction="column"
              isInvalid={showErrors && !issue}
              ariaLabel={t({
                id: 'article.actions.repair_article.report_repair_issue.title',
                message: 'Report a repair issue',
              })}
            >
              {repairIssues.map(({ id, label, onBehalfLabel }) => (
                <RadioBox
                  key={id}
                  value={id}
                  ariaLabel={i18n._(!iam.hasUserScopeOnArticle('workshop') ? onBehalfLabel : label)}
                  title={i18n._(!iam.hasUserScopeOnArticle('workshop') ? onBehalfLabel : label)}
                />
              ))}
            </RadioBoxGroup>
          </Label>
          <TextArea
            label={
              <>
                <Trans id="article.actions.repair_article.report_repair_issue.comment_title">
                  Comment
                </Trans>
                {!issueData?.requireComment && <OptionalLabel />}
              </>
            }
            ariaLabel={t({
              id: 'article.actions.repair_article.report_repair_issue.comment_placeholder',
              message: 'Write a comment...',
            })}
            value={comment ?? ''}
            rows={3}
            onChange={(event) => setComment(event.target.value)}
            placeholder={t({
              id: 'article.actions.repair_article.report_repair_issue.comment_placeholder',
              message: 'Write a comment...',
            })}
            error={
              showErrors && issueData?.requireComment && !comment
                ? t({
                    id: 'refuse-analysis-modal.comment-error',
                    message: 'Please write a comment',
                  })
                : undefined
            }
          />
        </Stack>
      </main>
      <Footer>
        <FooterButtons>
          <Button
            variant="danger"
            size="medium"
            onPress={() => {
              setShowErrors(true);

              if (!issue) {
                return;
              }

              if (issueData?.requireComment && !comment) {
                return;
              }

              onConfirm({ issue, comment });
            }}
            isLoading={isLoading}
          >
            <span style={{ textWrap: 'nowrap' }}>
              <Trans id="article.actions.repair_article.report_repair_issue.confirm">
                Report a repair issue
              </Trans>
            </span>
          </Button>
        </FooterButtons>
      </Footer>
    </>
  );
};
